import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../utils/api';
import { openExternalLink } from '../utils/openExternalLink';
import { 
  FaUserCircle, FaHeart, FaCreditCard, FaBell, FaSignOutAlt, FaCrosshairs, FaCrown, FaTimes, FaCog, FaPaypal, FaGooglePay, FaArrowRight, FaChartLine, FaShieldAlt
} from 'react-icons/fa';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faBolt, faCreditCard as faCard, faSpinner, faHistory, faCrosshairs, faVault, 
  faCrown as faCr, faChartBar, faMicrochip, faCubes, faHeart as faSolidHeart, faCog as faSettings,
  faLink
} from '@fortawesome/free-solid-svg-icons';
import { useWishlist } from '../context/WishlistContext';
import { useCurrency } from '../context/CurrencyContext';
import ProductCard from '../JSComponents/ProductCard';
import '../ProfileStyles/AdminCustomerProfilePage.css';

function CustomerProfilePage({ currentUser, onLogout, setCurrentUser }) {
  const navigate = useNavigate();
  const { wishlistItems, removeFromWishlist } = useWishlist();
  const { getCurrencySymbol } = useCurrency();
  const [selectedTab, setSelectedTab] = useState('overview');
  
  const [orders, setOrders] = useState([]);
  const [fullUser, setFullUser] = useState(null);
  const [showUpgradeForm, setShowUpgradeForm] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [snipes, setSnipes] = useState([]);
  const [isPro, setIsPro] = useState(currentUser?.subscription?.status === 'pro');
  const [isSyncing, setIsSyncing] = useState(false);
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [editForm, setEditProfileForm] = useState({ username: currentUser?.username || '', email: currentUser?.email || '' });
  const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });

  const stats = useMemo(() => {
    const totalPotentialSavings = wishlistItems.reduce((acc, item) => {
      if (item.originalPrice && item.productPrice < item.originalPrice) {
        return acc + (item.originalPrice - item.productPrice);
      }
      return acc;
    }, 0);

    const onSaleCount = wishlistItems.filter(item => item.originalPrice && item.productPrice < item.originalPrice).length;
    
    return {
      totalPotentialSavings,
      onSaleCount,
      totalItems: wishlistItems.length
    };
  }, [wishlistItems]);

  const formatPriceLocal = (price) => {
    if (!price) return '0.00';
    return `${getCurrencySymbol()}${parseFloat(price).toFixed(2)}`;
  };

  useEffect(() => {
    setIsPro(currentUser?.subscription?.status === 'pro');
    if (!currentUser) {
      navigate('/signin');
    } else {
      const fetchData = async () => {
        try {
          const [uRes, oRes] = await Promise.all([
            api.get('/api/users/me'),
            api.get('/api/orders/my-orders')
          ]);
          setFullUser(uRes.data);
          setOrders(Array.isArray(oRes.data) ? oRes.data : []);
          if (currentUser.subscription?.status === 'pro') {
            const sRes = await api.get('/api/alerts/snipe');
            setSnipes(sRes.data);
          }
        } catch (e) { console.error('PROFILE_DATA_ERROR'); }
      };
      fetchData();
    }
  }, [currentUser, navigate]);

  const handleAddSnipe = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());
    try {
      setIsSyncing(true);
      await api.post('/api/alerts/snipe', data);
      const sRes = await api.get('/api/alerts/snipe');
      setSnipes(sRes.data);
      e.target.reset();
      alert('SNIPE LOCKED');
    } catch (e) { alert('SNIPE ERROR'); }
    finally { setIsSyncing(false); }
  };

  const handleEditProfile = async (e) => {
    e.preventDefault();
    setIsProcessing(true);
    try {
      const response = await api.patch('/api/users/profile', { 
        firstName: editForm.username,
        email: editForm.email 
      });
      const updatedUser = { ...currentUser, ...response.data.user, username: editForm.username };
      setCurrentUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
      alert('Profile updated successfully');
      setShowEditProfile(false);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update profile');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      alert('Passwords do not match');
      return;
    }
    setIsProcessing(true);
    try {
      await api.patch('/api/users/password', {
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword
      });
      alert('Password updated successfully');
      setShowChangePassword(false);
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update password');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleLogoutClick = () => { onLogout(); navigate('/'); };
 
  const handlePurchaseAll = () => {
    if (wishlistItems.length === 0) return;
    
    wishlistItems.forEach((item, index) => {
      const url = item.affiliateUrl;
      if (url) {
        if (index === 0) {
          openExternalLink(url);
        } else {
          setTimeout(() => {
            openExternalLink(url);
          }, index * 500);
        }
      }
    });

    if (wishlistItems.length > 1) {
      alert(`Opening ${wishlistItems.length} items. Please allow pop-ups if only one tab opens.`);
    }
  };

  const renderContent = () => {
    switch (selectedTab) {
      case 'overview':
        return (
          <div className="terminal-dashboard animate-fade-in">
            <div className="dashboard-grid-hud">
              <div className="hud-card metric-primary">
                <div className="hud-card-edge"></div>
                <div className="hud-card-content">
                  <span className="hud-label"><FontAwesomeIcon icon={faBolt} /> TOTAL SAVINGS</span>
                  <div className="hud-value-wrap">
                    <span className="hud-value highlight">{formatPriceLocal(stats.totalPotentialSavings)}</span>
                    <span className="hud-trend up"><FaChartLine /></span>
                  </div>
                  <p className="hud-subtext">Potential savings across your watchlist</p>
                </div>
              </div>

              <div className="hud-card metric-accent disabled-card coming-soon-tooltip">
                <div className="hud-card-content">
                  <span className="hud-label"><FontAwesomeIcon icon={faCrosshairs} /> SNIPE CAPACITY</span>
                  <div className="hud-value-wrap">
                    <span className="hud-value greyed">0 / 0</span>
                    <span className="inline-coming-soon">COMING SOON</span>
                  </div>
                  <p className="hud-subtext">Automated inventory tracking</p>
                </div>
              </div>
            </div>

            <div className="dashboard-main-area">
              <div className="terminal-window watchlist-preview">
                <div className="terminal-header">
                  <div className="terminal-controls"><span></span><span></span><span></span></div>
                  <div className="terminal-title">RECENT SAVES</div>
                  <button onClick={() => setSelectedTab('saved-items')} className="terminal-action">View All <FaArrowRight /></button>
                </div>
                <div className="terminal-body">
                  {wishlistItems.length > 0 ? (
                    <div className="mini-watchlist-scroll">
                      {wishlistItems.slice(0, 4).map(item => (
                        <div key={item._id} className="watchlist-mini-node">
                          <img src={item.productImage || item.image} alt="" className="node-img" />
                          <div className="node-info">
                            <span className="node-brand">{item.productBrand}</span>
                            <span className="node-name">{item.productName}</span>
                          </div>
                          <div className="node-price">
                            <span className="current">{formatPriceLocal(item.productPrice)}</span>
                            {item.isAvailable === false && <span className="sold-out">SOLD OUT</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="terminal-empty">No items in watchlist yet</div>
                  )}
                </div>
              </div>

              <div className="pro-callout-module disabled-module coming-soon-tooltip">
                <div className="upgrade-hud-box">
                  <div className="scan-line"></div>
                  <div className="upgrade-content">
                    <h3 className="greyed">STOCKROOM PRO</h3>
                    <p>Priority snipes, price drop predictions, and exclusive early access signals.</p>
                    <button className="upgrade-trigger-btn" disabled>
                      COMING SOON
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      case 'snipes':
        return (
          <div className="snipe-terminal animate-fade-in">
             <div className="snipe-header-tech">
               <div className="radar-circle">
                 <div className="radar-sweep"></div>
               </div>
               <div className="snipe-title-wrap">
                 <h2>AUTO SNIPE</h2>
                 <p>Automated brand tracking and acquisition</p>
               </div>
             </div>
             <div className="snipe-console">
                <div className="console-input-row">
                   <form onSubmit={handleAddSnipe} className="brutalist-form">
                     <div className="input-group-tech">
                       <span className="input-prefix">BRAND:</span>
                       <input name="brand" placeholder="Enter brand name..." required />
                     </div>
                     <button type="submit" className="lock-target-btn">ADD TRACKER</button>
                   </form>
                </div>
                <div className="active-targets-list">
                  <div className="list-header-tech">
                    <span>BRAND</span>
                    <span>ACTIVITY</span>
                    <span>STATUS</span>
                  </div>
                  {snipes.length > 0 ? snipes.map((s, i) => (
                    <div key={i} className="target-row">
                      <span className="target-id">{s.brand.toUpperCase()}</span>
                      <div className="signal-strength"><div className="bar full"></div><div className="bar full"></div><div className="bar half"></div></div>
                      <span className="target-status active">SCANNING...</span>
                    </div>
                  )) : (
                    <div className="no-targets">Waiting for target acquisition...</div>
                  )}
                </div>
             </div>
          </div>
        );
      case 'saved-items':
        const totalOriginal = wishlistItems.reduce((sum, item) => sum + (item.originalPrice || item.productPrice || 0), 0);
        const totalSale = wishlistItems.reduce((sum, item) => sum + (item.productPrice || 0), 0);
        const totalSavings = totalOriginal - totalSale;

        return (
          <div className="vault-interface animate-fade-in">
            <div className="vault-header-tech">
              <span className="vault-count">({wishlistItems.length} items saved)</span>
            </div>
            
            {wishlistItems.length > 0 ? (
              <div className="vault-table-container">
                <div className="vault-table-header">
                  <span className="col-product">PRODUCT</span>
                  <span className="col-retailer">STORE</span>
                  <span className="col-size">SIZE</span>
                  <span className="col-price-orig">ORIGINAL</span>
                  <span className="col-price-sale">SALE</span>
                  <span className="col-status">STATUS</span>
                  <span className="col-actions">ACTIONS</span>
                </div>
                
                <div className="vault-table-body">
                  {wishlistItems.map(item => (
                    <div key={item._id} className={`vault-table-row ${item.isAvailable === false ? 'unavailable' : ''}`}>
                      <div className="col-product">
                        <Link to={`/product/${item.productId}`} className="product-cell-link">
                          <img src={item.productImage || item.image} alt="" className="row-thumb" />
                          <div className="product-cell-info">
                            <span className="row-brand">{item.productBrand}</span>
                            <span className="row-name">{item.productName}</span>
                          </div>
                        </Link>
                      </div>
                      <div className="col-retailer">
                        <span className="row-retailer-text">{item.merchantName || item.productBrand}</span>
                      </div>
                      <div className="col-size">
                        <span className="row-size-tag">{item.size || 'M'}</span>
                      </div>
                      <div className="col-price-orig">
                        <span className="row-price-orig-text">{formatPriceLocal(item.originalPrice || item.productPrice)}</span>
                      </div>
                      <div className="col-price-sale">
                        <span className="row-price-sale-text highlight">{formatPriceLocal(item.productPrice)}</span>
                      </div>
                      <div className="col-status">
                        {item.isAvailable === false ? (
                          <span className="row-status sold-out">SOLD OUT</span>
                        ) : (
                          <span className="row-status live">LIVE</span>
                        )}
                      </div>
                      <div className="col-actions">
                        <div className="row-action-btns">
                          <button 
                            className="row-action-btn link"
                            onClick={() => item.affiliateUrl && openExternalLink(item.affiliateUrl)}
                            title="Go to Retailer"
                          >
                            <FontAwesomeIcon icon={faLink} />
                          </button>
                          <button 
                            className="row-action-btn delete"
                            onClick={() => removeFromWishlist(item._id)}
                            title="Remove"
                          >
                            <FaTimes />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="vault-table-footer">
                  <div className="footer-label">TOTAL WATCHLIST VALUE</div>
                  <div className="footer-orig-sum">{formatPriceLocal(totalOriginal)}</div>
                  <div className="footer-sale-sum highlight">{formatPriceLocal(totalSale)}</div>
                  <div className="footer-savings">
                      TOTAL SAVINGS: <span className="highlight">{formatPriceLocal(totalSavings)}</span>
                  </div>
                </div>

                <div className="vault-table-actions">
                  <button className="vault-purchase-btn" onClick={handlePurchaseAll}>
                    PURCHASE ALL SAVED ITEMS
                  </button>
                </div>
              </div>
            ) : (
              <div className="terminal-empty">Your watchlist is currently empty</div>
            )}
          </div>
        );
      case 'account':
        return (
          <div className="account-settings-terminal animate-fade-in">
            <div className="settings-grid">
              <div className="settings-card">
                <div className="settings-card-header">
                  <span className="settings-icon"><FaUserCircle /></span>
                  <h3>PROFILE INFO</h3>
                </div>
                <div className="settings-card-body">
                  {!showEditProfile ? (
                    <>
                      <div className="settings-row">
                        <span className="label">Username</span>
                        <span className="value">{currentUser.username}</span>
                      </div>
                      <div className="settings-row">
                        <span className="label">Email Address</span>
                        <span className="value">{currentUser.email}</span>
                      </div>
                      <button className="settings-action-btn" onClick={() => setShowEditProfile(true)}>EDIT PROFILE</button>
                    </>
                  ) : (
                    <form onSubmit={handleEditProfile} className="settings-form">
                      <div className="input-group-tech">
                        <span className="input-prefix">USERNAME:</span>
                        <input 
                          value={editForm.username} 
                          onChange={(e) => setEditProfileForm({...editForm, username: e.target.value})}
                          required 
                        />
                      </div>
                      <div className="input-group-tech" style={{ marginTop: '10px' }}>
                        <span className="input-prefix">EMAIL:</span>
                        <input 
                          type="email"
                          value={editForm.email} 
                          onChange={(e) => setEditProfileForm({...editForm, email: e.target.value})}
                          required 
                        />
                      </div>
                      <div className="form-actions-row">
                        <button type="submit" className="lock-target-btn" disabled={isProcessing}>
                          {isProcessing ? 'SAVING...' : 'SAVE CHANGES'}
                        </button>
                        <button type="button" className="cancel-btn-tech" onClick={() => setShowEditProfile(false)}>CANCEL</button>
                      </div>
                    </form>
                  )}
                </div>
              </div>

              <div className="settings-card highlight disabled-card coming-soon-tooltip">
                <div className="settings-card-header">
                  <span className="settings-icon"><FaCrown /></span>
                  <h3>MEMBERSHIP STATUS</h3>
                </div>
                <div className="settings-card-body">
                  <div className="membership-status">
                    <div className="status-value-row">
                      <span className={`status-value ${isPro ? 'pro' : 'greyed'}`}>
                        {isPro ? 'STOCKROOM PRO' : 'STANDARD TIER'}
                      </span>
                      {!isPro && <span className="inline-coming-soon">COMING SOON</span>}
                    </div>
                  </div>
                  {!isPro && (
                    <button className="settings-action-btn primary" disabled>COMING SOON</button>
                  )}
                </div>
              </div>

              <div className="settings-card">
                <div className="settings-card-header">
                  <span className="settings-icon"><FaShieldAlt /></span>
                  <h3>SECURITY</h3>
                </div>
                <div className="settings-card-body">
                  {!showChangePassword ? (
                    <>
                      <div className="settings-row">
                        <span className="label">Password</span>
                        <span className="value">••••••••</span>
                      </div>
                      <button className="settings-action-btn" onClick={() => setShowChangePassword(true)}>CHANGE PASSWORD</button>
                    </>
                  ) : (
                    <form onSubmit={handleChangePassword} className="settings-form">
                      <div className="input-group-tech">
                        <span className="input-prefix">CURRENT:</span>
                        <input 
                          type="password"
                          value={passwordForm.currentPassword} 
                          onChange={(e) => setPasswordForm({...passwordForm, currentPassword: e.target.value})}
                          required 
                        />
                      </div>
                      <div className="input-group-tech" style={{ marginTop: '10px' }}>
                        <span className="input-prefix">NEW:</span>
                        <input 
                          type="password"
                          value={passwordForm.newPassword} 
                          onChange={(e) => setPasswordForm({...passwordForm, newPassword: e.target.value})}
                          required 
                        />
                      </div>
                      <div className="input-group-tech" style={{ marginTop: '10px' }}>
                        <span className="input-prefix">CONFIRM:</span>
                        <input 
                          type="password"
                          value={passwordForm.confirmPassword} 
                          onChange={(e) => setPasswordForm({...passwordForm, confirmPassword: e.target.value})}
                          required 
                        />
                      </div>
                      <div className="form-actions-row">
                        <button type="submit" className="lock-target-btn" disabled={isProcessing}>
                          {isProcessing ? 'UPDATING...' : 'UPDATE PASSWORD'}
                        </button>
                        <button type="button" className="cancel-btn-tech" onClick={() => setShowChangePassword(false)}>CANCEL</button>
                      </div>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      default: return null;
    }
  };

  return (
    <div className="command-page-master technical-theme">
      <div className="command-sidebar">
        <div className="command-user-profile">
          <div className="hex-avatar">
            <div className="hex-inner">
              {(fullUser?.firstName?.[0] || currentUser.username?.[0] || 'M').toUpperCase()}
            </div>
            <div className="hex-ring"></div>
          </div>
          <h3 className="user-title">{(fullUser?.firstName || currentUser.username || 'MEMBER').toUpperCase()}</h3>
          <div className="access-tag">{isPro ? 'PRO MEMBER' : 'FREE ACCOUNT'}</div>
        </div>
        
        <nav className="command-nav">
          <button className={selectedTab === 'overview' ? 'active' : ''} onClick={() => setSelectedTab('overview')}>
            <span className="btn-icon"><FontAwesomeIcon icon={faChartBar} /></span>
            <span className="btn-text">DASHBOARD</span>
          </button>
          <button className="disabled-nav-btn coming-soon-side-wrap" disabled>
            <span className="btn-icon"><FontAwesomeIcon icon={faCrosshairs} /></span>
            <div className="btn-text-row">
              <span className="btn-text">AUTO SNIPE</span>
              <span className="side-coming-soon">COMING SOON</span>
            </div>
          </button>
          <button className={selectedTab === 'saved-items' ? 'active' : ''} onClick={() => setSelectedTab('saved-items')}>
            <span className="btn-icon"><FontAwesomeIcon icon={faSolidHeart} /></span>
            <span className="btn-text">WATCHLIST</span>
          </button>
          <button className={selectedTab === 'account' ? 'active' : ''} onClick={() => setSelectedTab('account')}>
            <span className="btn-icon"><FontAwesomeIcon icon={faSettings} /></span>
            <span className="btn-text">SETTINGS</span>
          </button>
          <div className="sidebar-footer">
            <button className="logout-btn-technical" onClick={handleLogoutClick}>
              <span className="btn-icon"><FaSignOutAlt /></span>
              <span className="btn-text">LOGOUT</span>
            </button>
          </div>
        </nav>
      </div>
      
      <div className="command-viewport">
        <div className="viewport-overlay-grid"></div>
        <header className="viewport-header">
          <div className="header-meta">
            <span className="meta-bit">NODE: ACTIVE</span>
            <span className="meta-bit">STATUS: ONLINE</span>
          </div>
          <h1 className="header-title">
            {selectedTab === 'saved-items' ? 'WATCHLIST' : 
             selectedTab === 'account' ? 'SETTINGS' : 
             selectedTab.toUpperCase()}
          </h1>
        </header>
        <div className="viewport-content">{renderContent()}</div>
      </div>
    </div>
  );
}

export default CustomerProfilePage;
